import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  useUserAuthSessionStore,
  useUserStore,
  useProfileDraftStore,
  InitializeSessionProps,
} from '@/store'

interface UseUserAuthSessionProps {
  afterEndSession?: () => void
}

export const useUserAuthSession = (props?: UseUserAuthSessionProps) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { initializeSession, removeSession, token, isSessionOpen } =
    useUserAuthSessionStore()
  const { clearUserData } = useUserStore()
  const { clearProfileDraft } = useProfileDraftStore()

  const startSession = useCallback(
    async (sessionProps: InitializeSessionProps) => {
      await initializeSession(sessionProps)
    },
    [initializeSession],
  )

  const clearAuth = useCallback(async () => {
    if (token) {
      clearUserData()
      queryClient.clear()
    }
  }, [clearUserData, queryClient, token])

  const endSession = useCallback(async () => {
    await Promise.all([
      removeSession(),
      clearAuth(),
      clearProfileDraft(),
      // Note: In web we don't need GoogleSignin.signOut() like in React Native
    ])

    // Clear all cached data
    queryClient.clear()

    // Call afterEndSession callback if provided
    props?.afterEndSession?.()

    // If no callback provided, navigate to main page
    if (!props?.afterEndSession) {
      setTimeout(() => {
        navigate('/', { replace: true })
      }, 100)
    }
  }, [
    removeSession,
    clearAuth,
    clearProfileDraft,
    queryClient,
    props,
    navigate,
  ])

  return {
    startSession,
    endSession,
    token,
    isSessionOpen,
  }
}
